import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { HERO } from "@/lib/content";
import { EASE } from "./Reveal";
import { renderAA } from "./aa";
import HeroBeforeAfter from "./HeroBeforeAfter";

export const Hero = ({ onContact }) => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const textY = useTransform(scrollYProgress, [0, 1], [0, -50]);
  const visualY = useTransform(scrollYProgress, [0, 1], [0, 55]);

  return (
    <section ref={heroRef} id="top" className="relative bg-[#F7F5F0] overflow-hidden" data-testid="hero-section">
      <div className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full bg-[#9CA888]/15 blur-3xl" />
      <div className="absolute top-1/3 -left-48 w-[440px] h-[440px] rounded-full bg-[#8A5730]/[0.07] blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-32 sm:pt-40 pb-20 lg:pb-28">
        <div className="grid lg:grid-cols-[1fr_0.9fr] gap-12 lg:gap-16 items-center">
          <motion.div style={{ y: textY }}>
            <h1 className="font-display font-medium tracking-tight leading-[1.1] text-4xl sm:text-5xl" data-testid="hero-headline">
              {HERO.titleLines.map((line, i) => (
                <span key={i} className="block overflow-hidden pb-1.5 -mb-1.5">
                  <motion.span
                    className="block text-[#3E2818]"
                    initial={{ y: "112%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 1.05, delay: 0.18 + i * 0.14, ease: EASE }}
                  >
                    {line.aa ? renderAA(line.text) : line.text}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.62, ease: EASE }}
              className="mt-10 text-base sm:text-lg text-[#3E2818]/75 leading-relaxed max-w-xl"
              data-testid="hero-subtext"
            >
              {HERO.sub}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
              className="flex flex-wrap gap-4 mt-14"
            >
              <button data-testid="hero-talk-to-us-btn" onClick={() => onContact("Talk to us")} className="btn-primary !bg-[#211307] hover:!bg-[#D1633D] !border-[#211307] hover:!border-[#D1633D]">
                Talk to us <ArrowUpRight size={17} strokeWidth={2.2} />
              </button>
              <button data-testid="hero-book-demo-btn" onClick={() => onContact("Book a Demo")} className="btn-outline">
                Book a Demo
              </button>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.7, ease: EASE }}
            style={{ y: visualY }}
            className="relative z-[91] flex items-center justify-center"
            data-testid="hero-visual"
          >
            <HeroBeforeAfter className="w-full" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
